import useClassNames from '@medflex/hooks/useClassNames';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import DateOfBirthField from '../DateOfBirth/DateOfBirthField';
import Button from '../Button';
import InputField from '../InputField';
import FormErrorMessage from '../FormErrorMessage';

const CreateSubContactForm = ({
  onSubmit,
  readonly,
  initialValues,
  validationSchema,
  className,
}) => {
  const { t } = useTranslation('components');
  const classNames = useClassNames(() => ['flex', 'flex-col', className], [className]);
  const {
    register: registerFormElement,
    errors,
    control,
    handleSubmit: wrapSubmit,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  return (
    <form className={classNames} onSubmit={wrapSubmit(onSubmit)}>
      <label htmlFor="firstName" className="block mb-4 font-semibold">
        {t('CreateSubContactForm.firstName')}
        <InputField
          id="firstName"
          name="firstName"
          readOnly={readonly?.includes('firstName')}
          state={errors.firstName ? 'invalid' : 'default'}
          ref={registerFormElement}
          defaultValue={initialValues && initialValues.firstName}
        />
        {errors.firstName && <FormErrorMessage message={errors.firstName.message} />}
      </label>
      <label htmlFor="lastName" className="block mb-4 font-semibold">
        {t('CreateSubContactForm.lastName')}
        <InputField
          id="lastName"
          name="lastName"
          readOnly={readonly?.includes('lastName')}
          state={errors.lastName ? 'invalid' : 'default'}
          ref={registerFormElement}
          defaultValue={initialValues && initialValues.lastName}
        />
        {errors.lastName && <FormErrorMessage message={errors.lastName.message} />}
      </label>
      <label className="block mb-1 font-semibold leading-5 w-full" htmlFor="dateOfBirth">
        {t('CreateSubContactForm.dateOfBirth')}
      </label>
      <div className="flex flex-row space-x-2 mb-8">
        <DateOfBirthField
          name="dateOfBirth"
          control={control}
          readOnly={readonly?.includes('dateOfBirth')}
          state={errors.dateOfBirth ? 'invalid' : 'default'}
          defaultValue={initialValues && initialValues.dateOfBirth}
        />
      </div>
      {errors.birthday && <FormErrorMessage message={errors.birthday.message} />}

      <Button type="submit">{t('CreateSubContactForm.createSubcontact')}</Button>
    </form>
  );
};

CreateSubContactForm.propTypes = {
  onSubmit: PropTypes.func,
  initialValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
  }),
  readonly: PropTypes.arrayOf(PropTypes.oneOf(['firstName', 'lastName', 'dateOfBirth'])),
  validationSchema: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
  }),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
};

CreateSubContactForm.defaultProps = {
  onSubmit: () => {},
  initialValues: {},
  validationSchema: {},
  className: null,
  readonly: [],
};

export default CreateSubContactForm;
